import { Target, Flag, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useSiteImages } from "../hooks/use-site-images";

export default function About() {
  const { t } = useLang();
  const siteImage = useSiteImages();

  return (
    <div data-testid="about-page">
      <section className="bg-[#0891b2] text-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.3em] text-red-200 mb-4 font-semibold">{t.about.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl" data-testid="about-title">
            {t.about.title}
          </h1>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-slate-700 leading-relaxed text-lg">{t.about.body}</p>
          </div>
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
            <img src={siteImage("about_main_image", "https://images.pexels.com/photos/2977514/pexels-photo-2977514.jpeg")} alt="Kitchen" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-lg border-t-4 border-[#0891b2] shadow-sm" data-testid="vision-block">
            <Target className="w-10 h-10 text-[#0891b2] mb-4" />
            <h2 className="font-display text-2xl text-[#0891b2] mb-3">{t.about.visionTitle}</h2>
            <p className="text-slate-700 leading-relaxed">{t.about.vision}</p>
          </div>
          <div className="bg-white p-8 rounded-lg border-t-4 border-[#ef4444] shadow-sm" data-testid="mission-block">
            <Flag className="w-10 h-10 text-[#ef4444] mb-4" />
            <h2 className="font-display text-2xl text-[#ef4444] mb-3">{t.about.missionTitle}</h2>
            <p className="text-slate-700 leading-relaxed">{t.about.mission}</p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-3xl text-[#0891b2] mb-6">{t.about.whatWeDoTitle}</h2>
            <ul className="space-y-3">
              {t.about.whatWeDo.map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`what-we-do-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#ef4444] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-3xl text-[#0891b2] mb-6">{t.about.strengthsTitle}</h2>
            <ul className="space-y-3">
              {t.about.strengths.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#0891b2] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl text-[#0891b2] mb-8 text-center">{t.about.sectorsTitle}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(t.categories).map(([key, label]) => (
              <div key={key} className="bg-white border border-slate-200 rounded p-4 text-center text-sm text-slate-700 font-medium">
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
